import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import { Reveal, MaskReveal, FadeIn } from "../components/Reveal";
import { MetaLabel } from "../components/Bits";
import { IMAGES } from "../lib/brand";
import { setSeo } from "../lib/seo";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Home() {
  const [posts, setPosts] = useState([]);
  const [checklist, setChecklist] = useState({ first_name: "", email: "" });
  const [sending, setSending] = useState(false);
  const [checklistDone, setChecklistDone] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState("");

  useEffect(() => { setSeo({ title: "Marella Bolognesi — The Art of Estate Management · Miami, Florida", description: "Marella Bolognesi is a working Estate Manager in Miami, Florida. Private Estate Management and Il Salotto Digitale — courses, guides and tools for those entering and growing within the profession." }); }, []);
  useEffect(() => { api.get("/posts").then((r) => setPosts(r.data.slice(0, 3))).catch(() => {}); }, []);

  const requestChecklist = async (e) => {
    e.preventDefault();
    if (!checklist.first_name || !checklist.email) { toast.error("Please add your name and email."); return; }
    setSending(true);
    try {
      const { data } = await api.post("/checklist/request", checklist);
      const url = `${BACKEND}/api/checklist/download?token=${data.token}`;
      setDownloadUrl(url);
      window.open(url, "_blank");
      setChecklistDone(true);
    } catch (err) { toast.error(apiError(err)); } finally { setSending(false); }
  };

  return (
    <div data-testid="home-page">
      {/* ---------- 1 · HERO ---------- */}
      <section className="relative min-h-screen flex items-end overflow-hidden">
        <motion.div initial={{ scale: 1.15 }} animate={{ scale: 1 }} transition={{ duration: 1.8, ease: [0.19, 1, 0.22, 1] }} className="absolute inset-0 z-0">
          <img src={IMAGES.interior} alt="A sophisticated private residence" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/30 to-ink/20" />
        </motion.div>

        <div className="relative z-10 mx-auto max-w-[1600px] w-full px-6 lg:px-12 pb-16 lg:pb-24 pt-40 text-bg">
          <FadeIn delay={0.2}>
            <div className="flex items-center gap-3 mb-8">
              <span className="w-2 h-2 rounded-full bg-accent animate-pulse" />
              <span className="text-[11px] uppercase tracking-ultra text-bg/80">Estate Management · Private Service · Education</span>
            </div>
          </FadeIn>

          <h1 className="font-serif font-light text-[13vw] sm:text-[11vw] lg:text-[8vw] leading-[0.92] tracking-tight">
            <MaskReveal lines={["The Art of", "Estate Management"]} delay={0.35} />
          </h1>

          <div className="mt-14 lg:mt-20 grid lg:grid-cols-12 gap-10 lg:gap-16 items-end">
            <FadeIn delay={1.2} className="lg:col-span-6">
              <p className="text-lg lg:text-xl font-light text-bg/80 leading-relaxed max-w-xl">
                Estate management, private service and professional education — shaped by real experience inside exceptional homes.
              </p>
            </FadeIn>
            <FadeIn delay={1.35} className="lg:col-span-6 lg:col-start-7 flex flex-wrap items-center gap-6 lg:gap-8 lg:justify-end">
              <Link to="/services" data-testid="hero-estate-btn" className="group bg-bg text-ink px-8 py-4 text-[11px] uppercase tracking-[0.2em] hover:bg-surface transition-colors flex items-center gap-3">
                Explore Estate Management <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <Link to="/salotto-digitale" data-testid="hero-salotto-btn" className="group inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-bg/70 hover:text-bg transition-colors link-underline">
                Enter Il Salotto Digitale <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ---------- 2 · INTRODUCTION ---------- */}
      <section className="mx-auto max-w-[1600px] px-6 lg:px-12 py-24 lg:py-36">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-20">
          <Reveal className="lg:col-span-3"><MetaLabel>Introduction</MetaLabel></Reveal>
          <div className="lg:col-span-9 max-w-3xl">
            <Reveal>
              <p className="font-serif font-light text-2xl sm:text-3xl lg:text-[2.6rem] leading-[1.25] text-ink tracking-tight">
                Marella Bolognesi is a working Estate Manager with a background in international hospitality, events and design. Today, she manages private residences in Miami, bringing structure, discretion and thoughtful systems to the work that happens behind the scenes of a well-run home.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <Link to="/about" data-testid="intro-about-link" className="mt-10 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-ink link-underline">
                About Marella <ArrowUpRight size={15} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ---------- 3 · TWO WORLDS ---------- */}
      <section className="border-t border-line grid lg:grid-cols-2">
        {[
          { n: "01", label: "Private Estate Management", desc: "For private residences and principals — bringing structure, continuity and thoughtful oversight to the work behind a well-run home.", to: "/services", cta: "Explore Estate Management", img: IMAGES.mission },
          { n: "02", label: "Il Salotto Digitale", desc: "Courses, practical tools and professional resources for those entering — or already working within — Estate Management and private service.", to: "/salotto-digitale", cta: "Enter Il Salotto", img: IMAGES.salotto },
        ].map((w, i) => (
          <Reveal key={w.n} delay={i * 0.1} className={i === 0 ? "lg:border-r border-line" : ""}>
            <Link to={w.to} data-testid={`world-${w.n}`} className="group block h-full">
              <div className="relative overflow-hidden aspect-[16/10]">
                <img src={w.img} alt={w.label} className="w-full h-full object-cover object-top transition-transform duration-[1200ms] ease-out group-hover:scale-105" />
                <div className="absolute inset-0 bg-ink/10 group-hover:bg-ink/0 transition-colors" />
              </div>
              <div className="p-8 lg:p-16">
                <span className="font-serif text-accent text-2xl">{w.n}</span>
                <h2 className="mt-4 font-serif font-light text-3xl lg:text-5xl text-ink tracking-tight">{w.label}</h2>
                <p className="mt-5 text-base lg:text-lg font-light text-ink-soft leading-relaxed max-w-md">{w.desc}</p>
                <span className="mt-8 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-ink">
                  {w.cta} <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                </span>
              </div>
            </Link>
          </Reveal>
        ))}
      </section>

      {/* ---------- 4 · IL SALOTTO INTRODUCTION ---------- */}
      <section className="border-t border-line bg-surface">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12 py-24 lg:py-36">
          <Reveal><MetaLabel>Il Salotto Digitale</MetaLabel></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-8 font-serif font-light text-4xl sm:text-5xl lg:text-[4.5rem] leading-[1.02] text-ink tracking-tight max-w-4xl">
              A place to learn the work behind exceptional homes.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 text-lg font-light text-ink-soft leading-relaxed max-w-2xl">
              Il Salotto Digitale is Marella's curated collection of Estate Management courses, guides, tools and professional resources — each one drawn from real experience inside the homes she manages.
            </p>
          </Reveal>

          <div className="mt-16 grid md:grid-cols-3 border-t border-line">
            {[
              { key: "begin", n: "01", k: "Begin", v: "Career & introductory resources" },
              { key: "learn", n: "02", k: "Learn", v: "Courses & education" },
              { key: "use", n: "03", k: "Use", v: "Templates, guides & tools" },
            ].map((c, i) => (
              <Reveal key={c.key} delay={i * 0.08} className="border-b md:border-b-0 md:border-r last:border-r-0 border-line">
                <Link
                  to={`/salotto-digitale?c=${c.key}`}
                  data-testid={`salotto-category-${c.key}`}
                  className="group block h-full py-8 md:py-10 md:px-8 first:md:pl-0 transition-colors duration-300 hover:bg-surface/60"
                >
                  <span className="font-serif text-accent text-lg">{c.n}</span>
                  <div className="mt-2 flex items-center gap-3">
                    <p className="font-serif text-3xl lg:text-4xl text-ink transition-transform duration-300 group-hover:translate-x-1">{c.k}</p>
                    <ArrowUpRight size={20} className="text-ink opacity-0 -translate-x-1 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0" />
                  </div>
                  <p className="mt-3 text-sm uppercase tracking-[0.15em] text-ink-soft">{c.v}</p>
                </Link>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.15}>
            <Link to="/salotto-digitale" data-testid="salotto-intro-cta" className="mt-14 inline-flex items-center gap-3 bg-ink text-bg px-8 py-4 text-[11px] uppercase tracking-[0.2em] hover:bg-[#33312E] transition-colors group">
              Explore Il Salotto Digitale <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ---------- 5 · FEATURED COURSE ---------- */}
      <section className="mx-auto max-w-[1600px] px-6 lg:px-12 py-24 lg:py-36">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <Reveal className="lg:col-span-5">
            <div className="overflow-hidden aspect-[4/5]">
              <img src={IMAGES.livingWell} alt="How to Start as an Estate Manager" className="w-full h-full object-cover" />
            </div>
          </Reveal>
          <div className="lg:col-span-7">
            <Reveal><p className="text-[11px] uppercase tracking-ultra text-accent">Featured Course · $149</p></Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-6 font-serif font-light text-4xl lg:text-6xl text-ink leading-[1.02] tracking-tight">How to Start as an Estate Manager</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 text-lg font-light text-ink-soft leading-relaxed max-w-xl">
                For those drawn to Estate Management but unsure where to begin — a practical introduction to the profession and a clear roadmap for taking your first steps.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-10 grid sm:grid-cols-3 gap-8 border-t border-line pt-8">
                {[
                  { k: "Who it's for", v: "Aspiring Estate Managers, Personal Assistants and hospitality professionals considering private service." },
                  { k: "What it clarifies", v: "What the Estate Manager role involves, what private households expect, and how the profession works behind the scenes." },
                  { k: "You'll leave knowing", v: "Where to begin, what to prepare, and the next steps to take toward the profession." },
                ].map((b) => (
                  <div key={b.k}>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-accent">{b.k}</p>
                    <p className="mt-3 text-sm font-light text-ink-soft leading-relaxed">{b.v}</p>
                  </div>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <Link to="/course/how-to-start-as-an-estate-manager" data-testid="home-discover-course" className="mt-10 inline-flex items-center gap-3 border border-ink text-ink px-8 py-4 text-[11px] uppercase tracking-[0.2em] hover:bg-ink hover:text-bg transition-colors group">
                Explore the Course <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ---------- 6 · FREE RESOURCE ---------- */}
      <section className="border-t border-line bg-ink text-bg">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-[11px] uppercase tracking-ultra text-accent">A practical resource from inside the work</p>
            <h2 className="mt-6 font-serif font-light text-4xl lg:text-5xl leading-tight tracking-tight">Hurricane Preparedness Checklist</h2>
            <p className="mt-6 text-base font-light text-bg/70 leading-relaxed max-w-md">
              Preparedness is one of the responsibilities behind managing a residence well. This checklist is based on the considerations I use when preparing a home for severe weather.
            </p>
          </div>

          {checklistDone ? (
            <div data-testid="checklist-success" className="lg:justify-self-end w-full max-w-md">
              <div className="w-12 h-12 border border-bg/40 flex items-center justify-center"><Check size={22} strokeWidth={1.5} /></div>
              <h3 className="mt-6 font-serif font-light text-3xl tracking-tight">It's on its way.</h3>
              <p className="mt-4 text-base font-light text-bg/70 leading-relaxed">Your checklist is downloading now. You've also joined <span className="text-bg">Notes from Il Salotto</span>.</p>
              <a href={downloadUrl} data-testid="success-redownload" className="mt-3 inline-block text-[11px] uppercase tracking-[0.2em] text-bg/70 hover:text-bg link-underline">Download again</a>
              <div className="mt-8 border-t border-bg/20 pt-8">
                <p className="text-[11px] uppercase tracking-ultra text-accent">While you're here</p>
                <Link to="/salotto-digitale" data-testid="success-salotto" className="mt-4 inline-flex items-center gap-3 bg-bg text-ink px-8 py-4 text-[11px] uppercase tracking-[0.2em] hover:bg-surface transition-colors group">
                  Explore Il Salotto Digitale <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={requestChecklist} data-testid="checklist-form" className="lg:justify-self-end w-full max-w-md space-y-6">
              <div>
                <label className="text-[11px] uppercase tracking-[0.2em] text-bg/60">First Name</label>
                <input data-testid="checklist-firstname" value={checklist.first_name} onChange={(e) => setChecklist({ ...checklist, first_name: e.target.value })}
                  className="mt-2 w-full bg-transparent border-b border-bg/30 focus:border-bg outline-none py-3 text-bg text-lg font-light transition-colors placeholder:text-bg/40" placeholder="Your first name" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.2em] text-bg/60">Email</label>
                <input data-testid="checklist-email" type="email" value={checklist.email} onChange={(e) => setChecklist({ ...checklist, email: e.target.value })}
                  className="mt-2 w-full bg-transparent border-b border-bg/30 focus:border-bg outline-none py-3 text-bg text-lg font-light transition-colors placeholder:text-bg/40" placeholder="you@email.com" />
              </div>
              <button type="submit" disabled={sending} data-testid="checklist-submit"
                className="group w-full bg-bg text-ink py-4 text-[11px] uppercase tracking-[0.2em] hover:bg-surface transition-colors flex items-center justify-center gap-3 disabled:opacity-60">
                {sending ? "Sending…" : "Send Me the Checklist"} <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
              </button>
            </form>
          )}
        </div>
      </section>

      {/* ---------- 7 · JOURNAL ---------- */}
      {posts.length > 0 && (
        <section className="mx-auto max-w-[1600px] px-6 lg:px-12 py-24 lg:py-36">
          <div className="flex items-end justify-between mb-12">
            <Reveal><h2 className="font-serif font-light text-4xl lg:text-6xl text-ink tracking-tight">From the Journal</h2></Reveal>
            <Reveal delay={0.1}>
              <Link to="/blog" data-testid="home-journal-cta" className="text-[11px] uppercase tracking-[0.2em] text-ink link-underline inline-flex items-center gap-2">Read the Journal <ArrowUpRight size={14} /></Link>
            </Reveal>
          </div>
          <div className="border-t border-line">
            {posts.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.06}>
                <Link to={`/blog/${p.slug}`} data-testid={`home-post-${i}`} className="group grid grid-cols-12 gap-4 items-center py-8 lg:py-10 border-b border-line">
                  <span className="col-span-2 lg:col-span-1 font-serif text-2xl text-accent">{String(i + 1).padStart(2, "0")}</span>
                  <span className="col-span-10 lg:col-span-2 text-[11px] uppercase tracking-[0.2em] text-ink-soft">{p.date}</span>
                  <h3 className="col-span-12 lg:col-span-8 font-serif font-light text-2xl lg:text-4xl text-ink leading-tight tracking-tight group-hover:text-accent transition-colors">{p.title}</h3>
                  <span className="hidden lg:flex col-span-1 justify-end"><ArrowUpRight className="text-ink transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" size={22} /></span>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* ---------- 8 · CLOSING ---------- */}
      <section className="border-t border-line grid lg:grid-cols-2">
        <div className="relative overflow-hidden min-h-[50vh] lg:min-h-[80vh]">
          <img src={IMAGES.portrait} alt="Marella Bolognesi" className="absolute inset-0 w-full h-full object-cover object-top" />
        </div>
        <div className="flex items-center bg-surface">
          <Reveal className="px-6 lg:px-20 py-20 lg:py-0">
            <MetaLabel>Marella Bolognesi</MetaLabel>
            <h2 className="mt-8 font-serif font-light text-4xl lg:text-6xl text-ink leading-[1.05] tracking-tight">
              What I learn through the work, I share through Il Salotto.
            </h2>
            <p className="mt-8 text-lg font-light text-ink-soft leading-relaxed max-w-md">
              Il Salotto Digitale is where I turn real Estate Management experience into practical education, tools and resources for those entering — and growing within — the profession.
            </p>
            <Link to="/salotto-digitale" data-testid="closing-cta" className="mt-10 inline-flex items-center gap-3 bg-ink text-bg px-8 py-4 text-[11px] uppercase tracking-[0.2em] hover:bg-[#33312E] transition-colors group">
              Enter Il Salotto <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
